import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import api from "config/axiosConfig";
import { Action, Sort, SortField, SortOrder } from "util/constants";
import { useUser } from "util/UserContext";
import ActionCardRefactored from "./ActionCardRefactored";
import LoadingSkeleton from "ui/LoadingSkeleton";
import ScrollHideContainer from "ui/ScrollHideContainer";
import { SelectTrigger, SelectValue, SelectItem, SelectContent, Select } from "ui/InputSelect";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import AddButton from "ui/AddButton";
import SpillTheTeaModal from "components/Posts/SpillTheTeaModal";
import { AxiosError } from "axios";
import EmptyState from "ui/EmptyState";
import { apiRoutes } from "util/apiRoutes";

interface Props {
  initialSort?: Sort;
}

const ActionsList = ({ initialSort = "soonest" }: Props) => {
  const { currentUser, setShowAuthModal } = useUser();
  const [actions, setActions] = useState<Action[]>([]);
  const [error, setError] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isPrivateFilter, setIsPrivateFilter] = useState<"All" | "public" | "private">("All");
  const [timeFilter, setTimeFilter] = useState<"All" | "upcoming" | "past">("upcoming");
  const [sortField, setSortField] = useState<SortField>("start_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Intersection observer for infinite scrolling
  const [loadMoreRef, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // Initial load ref
  const [initialLoadRef, initialInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const LIMIT = 10;

  useEffect(() => {
    switch (initialSort) {
      case "top":
        setSortField("participant_count");
        setSortOrder("desc");
        break;
      case "new":
        setSortField("created_at");
        setSortOrder("desc");
        break;
      case "soonest":
        setSortField("start_at");
        setSortOrder("asc");
        break;
      default:
        setSortField("start_at");
        setSortOrder("asc");
        break;
    }
  }, [initialSort]);

  const fetchActions = async (page: number, isInitialLoad: boolean = false) => {
    if (isInitialLoad) {
      setIsLoading(true);
    }

    try {
      const offset = (page - 1) * LIMIT;
      const orderBy = sortField === "start_at" ? "start_at" : sortField === "created_at" ? "created_at" : "created_at";
      const orderDir = sortOrder;

      const response = await api.get<Action[]>(apiRoutes.posts.root, {
        params: {
          kind: "action",
          limit: LIMIT,
          offset,
          orderBy,
          orderDir,
        },
      });

      const newActions = response.data || [];

      if (isInitialLoad || page === 1) {
        setActions(newActions);
      } else {
        setActions(prevActions => [...prevActions, ...newActions]);
      }
      
      setHasMore(newActions.length === LIMIT);
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) {
        setError("404: Failed to load actions. Please try again later.");
      } else {
        setError("Failed to load actions. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Reset pagination when filters change
    setPageNumber(1);
    setActions([]);
    if (initialInView) {
      fetchActions(1, true);
    }
    //eslint-disable-next-line
  }, [sortField, sortOrder, initialInView]);

  useEffect(() => {
    if (inView && hasMore && !isLoading && pageNumber > 1) {
      fetchActions(pageNumber);
    }
    //eslint-disable-next-line
  }, [inView, hasMore, isLoading, pageNumber]);

  const loadMoreActions = () => {
    if (hasMore && !isLoading) {
      setPageNumber(prevPage => prevPage + 1);
    }
  };

  useEffect(() => {
    if (inView && hasMore && !isLoading) {
      loadMoreActions();
    }
    //eslint-disable-next-line
  }, [inView, hasMore, isLoading]);

  const handleSortChange = (newSort: Sort) => {
    switch (newSort) {
      case "top":
        setSortField("participant_count");
        setSortOrder("desc");
        break;
      case "new":
        setSortField("created_at");
        setSortOrder("desc");
        break;
      case "soonest":
        setSortField("start_at");
        setSortOrder("asc");
        break;
    }
  };

  const handleAddActionButtonClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleActionAdded = async () => {
    try {
      setPageNumber(1);
      await fetchActions(1, true);
    } catch (err) {
      setError("Failed to refresh actions. Please reload the page.");
    }
  };

  // Filter actions
  const filteredActions = actions.filter((action) => {
    // Filter by privacy
    if (isPrivateFilter !== "All") {
      const isPrivate = action?.properties?.is_private || false;
      if (isPrivateFilter === "private" && !isPrivate) return false;
      if (isPrivateFilter === "public" && isPrivate) return false;
    }

    // Filter by time
    if (timeFilter !== "All") {
      const now = new Date();
      const startDate = action?.properties?.start_at ? new Date(action.properties.start_at) : null;
      
      if (timeFilter === "upcoming" && startDate && startDate < now) return false;
      if (timeFilter === "past" && (!startDate || startDate >= now)) return false;
    }

    return true;
  });

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find any actions." 
        buttonText="Go to Home" 
        buttonHref="/" 
      /> : 
      <ServerErrorPage />;
  }

  return (
    <div className="actions-list" ref={initialLoadRef}>
      <ScrollHideContainer 
        className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
        hideOffset={115}
        threshold={114}
      >
        <div className="flex flex-col space-y-1 mb-1">
          <div className="flex mb-1">
            <AddButton
              tooltipTitle="Add action"
              buttonText="Action"
              className="w-full"
              onClick={handleAddActionButtonClick}
            />
          </div>
          <div className="flex gap-1 pb-1">
            <Select onValueChange={(value: Sort) => handleSortChange(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="soonest">Soonest</SelectItem>
                <SelectItem value="top">Most Popular</SelectItem>
                <SelectItem value="new">Newest Added</SelectItem>
              </SelectContent>
            </Select>
            <Select onValueChange={(value) => setTimeFilter(value as "All" | "upcoming" | "past")}>
              <SelectTrigger>
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Actions</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="past">Past</SelectItem>
              </SelectContent>
            </Select>
            <Select onValueChange={(value) => setIsPrivateFilter(value as "All" | "public" | "private")}>
              <SelectTrigger>
                <SelectValue placeholder="Privacy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </ScrollHideContainer>
      
      <div className="mt-0">
        {isLoading && pageNumber === 1 ? (
          <LoadingSkeleton variant="entity-card" count={6} />
        ) : filteredActions.length === 0 ? (
          <EmptyState
            title="No actions yet"
            subtitle="Be the first to add an action!"
            className="m-4"
          />
        ) : (
            <div className="entity-divider flex flex-col">
            {filteredActions.map((action) => (
              <div key={action.id}>
                <ActionCardRefactored
                    key={action.id}
                    action={action}
                    truncateDescription={true}
                    isActionProfilePage={false}
                    isEntitySection={false}
                />
              </div>
            ))}
            {hasMore && (
              <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
                {isLoading && pageNumber > 1 && <LoadingSkeleton count={1} />}
              </div>
            )}
          </div>
        )}
      </div>
      
      {openModal && (
        <SpillTheTeaModal
          open={openModal}
          onClose={handleCloseModal}
          onActionAdded={handleActionAdded}
          initialSelectedIssue={null}
          customTitle="Add action"
          fixedPostType="action"
          hidePostTypeButtons={true}
        />
      )}
    </div>
  );
};

export default ActionsList;
